import React from 'react';
import '../../styles/components/AsperaLeftNav.scss';
import { withRouter } from 'react-router-dom';
import { SideNavMenu, SideNavMenuItem, SideNavItems, SideNavLink, Button, Theme, SideNavDivider } from '../Carbon';
import { openNewTab, altKeysUsed, resetTableStore } from '../../helpers/starterHelpers';
import { language } from '../../services/LanguageService';
import { tracking } from '../../services/TrackingService';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { observableTimeout } from '../../helpers/starterHelpers';
/**
 * React Component
 *
 * The AsperaLeftNav component renders the left navigation bar for Aspera
 *
 * @example
 * ```javascript
 * <AsperaLeftNav />
 * ```
 */
export const AsperaLeftNav = withRouter(class AsperaLeftNav extends React.Component {
    constructor() {
        super(...arguments);
        this.items = [];
        this.subManager = new SubManager();
        this.lastOpenCheckSubId = '';
        this.asperaHasChildrenClass = 'aspera-left-nav--has-active-child';
        this.lastRoute = '';
        this.handleFinalClick = (event, item) => {
            const { history } = this.props;
            resetTableStore();
            if (typeof item.onClick === 'function') {
                item.onClick(item, event);
            }
            if (item.route) {
                const newTab = altKeysUsed(event);
                newTab ? openNewTab(item.route) : history.push(item.route);
            }
            this.setExpanded();
            tracking.track('Navigate UI', { action: 'Clicked Left Nav Item', actionType: item.title, successFlag: true });
        };
        this.handleClick = (event, item) => {
            event.preventDefault();
            tracking.checkDirtyForms(() => this.handleFinalClick(event, item));
        };
        this.setExpanded = (_event, expanded = false) => {
            const { setLeftNav } = this.props;
            if (typeof setLeftNav === 'function')
                setLeftNav(expanded);
        };
        /**
         * Carbon has no way to set open after render (tried `isSideNavExpanded` but did not work)
         * Only way to make it open after render is to click it.
         */
        this.triggerOpenCheck = () => {
            this.subManager.unsubscribe(this.lastOpenCheckSubId);
            this.lastOpenCheckSubId = this.subManager.setSubscription(observableTimeout(500).subscribe({
                next: () => {
                    const foundElements = document.querySelectorAll(`.${this.asperaHasChildrenClass} > button[aria-expanded="false"]`);
                    foundElements.forEach(element => {
                        if (element && typeof element.click === 'function') {
                            element.click();
                        }
                    });
                },
                error: error => {
                    new AsperaError('AsperaLeftNav: encountered error updating open children', error);
                },
            }));
        };
    }
    checkIfActive(navItem) {
        const { history } = this.props;
        if (navItem.route && ((navItem.exact && history.location.pathname === navItem.route) || (!navItem.exact && history.location.pathname.indexOf(navItem.route || '') === 0))) {
            return true;
        }
        if (Array.isArray(navItem.exactExclude) && history.location.pathname.indexOf(navItem.route || '') === 0) {
            const foundExcludeMatch = navItem.exactExclude.filter(item => {
                return history.location.pathname.indexOf(item) > -1;
            });
            return !foundExcludeMatch.length;
        }
        return false;
    }
    getItems() {
        return this.items.filter(item => !item.hidden).map((item, key) => {
            if (item.divider) {
                return React.createElement(SideNavDivider, { key: `divider-${key}` });
            }
            if (item.children) {
                let hasActiveChildren = false;
                const childrenComponents = item.children.filter(item => !item.hidden).map((child, index) => {
                    const activeCheck = this.checkIfActive(child);
                    if (activeCheck)
                        hasActiveChildren = true;
                    return (React.createElement(SideNavMenuItem, { key: `${key}-${index}`, isActive: activeCheck, "aria-selected": activeCheck, 
                        // eslint-disable-next-line
                        role: "tab", "data-auto": `left-nav-item-${child.route || child.title}`, href: child.route || '#', onClick: (event) => {
                            this.handleClick(event, child);
                        } }, child.title));
                });
                return (React.createElement(SideNavMenu, { key: key, renderIcon: item.icon, title: item.title, defaultExpanded: item.initiallyOpen || hasActiveChildren, className: `${hasActiveChildren ? this.asperaHasChildrenClass : ''}` }, childrenComponents));
            }
            else {
                return (React.createElement(SideNavLink, { className: this.checkIfActive(item) ? 'cds--side-nav__link--current' : '', key: key, href: "#", "data-auto": `left-nav-item-${item.route || item.title}`, onClick: (event) => {
                        this.handleClick(event, item);
                    }, renderIcon: item.icon }, item.title));
            }
        });
    }
    getButton() {
        const { actionButton } = this.props;
        if (actionButton) {
            return React.createElement(Button, { className: "left-nav-action", "data-auto": "left-nav-action-button", onClick: () => {
                    this.setExpanded();
                    actionButton.onClick();
                }, kind: actionButton.type, renderIcon: actionButton.icon }, actionButton.title);
        }
    }
    getDropdownMenu() {
        const { dropdownMenu } = this.props;
        if (dropdownMenu) {
            return (React.createElement("div", { className: "aspera-left-nav-dropdown" }, dropdownMenu));
        }
    }
    getAdvertisement() {
        const { leftNavAdvertisement } = this.props;
        if (leftNavAdvertisement) {
            return (React.createElement("div", { className: "aspera-left-nav-advertisement" }, leftNavAdvertisement));
        }
        return null;
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidUpdate() {
        const { location } = this.props;
        if ((location === null || location === void 0 ? void 0 : location.pathname) !== this.lastRoute) {
            this.triggerOpenCheck();
        }
        this.lastRoute = location.pathname;
    }
    componentDidMount() {
        const { itemsSubscriber } = this.props;
        this.subManager.setSubscription(itemsSubscriber.subscribe({
            next: items => {
                this.subManager.unsubscribe(this.lastOpenCheckSubId);
                this.items = items || [];
                this.setState({}, () => {
                    this.triggerOpenCheck();
                });
            },
            error: error => {
                new AsperaError('AsperaLeftNav: Unable to monitor for left nav updates', error);
            },
        }));
    }
    render() {
        const { className, hideLeftNav, expanded, headerIsHidden } = this.props;
        return (React.createElement(Theme, { theme: "g100", className: `aspera-left-nav ${className ? className : ''} ${hideLeftNav ? 'hide-left-nav' : ''}` },
            React.createElement("nav", { className: `left-nav-wrapper ${headerIsHidden ? 'left-nav-no-header' : ''} ${expanded ? 'cds--side-nav--expanded' : ''} cds--side-nav__navigation left-nav-wrapper cds--side-nav cds--side-nav--ux`, "aria-label": language.get('leftNav.sideNavigation') },
                this.getDropdownMenu(),
                this.getButton(),
                React.createElement(SideNavItems, null, this.getItems()),
                this.getAdvertisement())));
    }
});
